import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ManageTagsFormDialog } from "components/ManageTags/ManageTagsFormDialog";
import { EntityUpdate } from "components/ManageTags/types";
import WasteLineUnifiedTagPicker from "components/WasteLineUnifiedTagPicker";
import { gql } from "generated-graphql";
import pluralize from "pluralize";
import { useCallback, useState } from "react";

import { wasteStreamTagChanges } from "./wasteStreamTagChanges";

export const UPDATE_WASTE_STREAM_TAGS_MUTATION = gql(`
  mutation UpdateWasteStreamTags($input: UpdateWasteStreamTagsInput!) {
    updateWasteStreamTags(input: $input)
  }
`);

// Shared with line items: both use Waste tags, so recent picks carry over.
const LOCAL_STORAGE_KEY = "waste-line-unified-recent-tags";

type WasteStreamTagsRow = { id: string; tags: { id: string; name: string }[] };

type ManageWasteStreamTagsDialogProps = {
  tenantId: string;
  selectedRows: WasteStreamTagsRow[];
  onClose: () => void;
  onSaved?: () => void;
};

/** Staff-only: add and remove tags on one or more waste streams. */
export function ManageWasteStreamTagsDialog({
  tenantId,
  selectedRows,
  onClose,
  onSaved,
}: ManageWasteStreamTagsDialogProps) {
  const alerts = useAlerts();
  const [showConfirmation, setShowConfirmation] = useState(false);
  // Every open view that filters or shows by tag: stream and line grids, the
  // facility shipment charts, and the drawer's shipments chart.
  const [updateWasteStreamTags, { loading }] = useMutation(UPDATE_WASTE_STREAM_TAGS_MUTATION, {
    refetchQueries: [
      "TenantWasteStreams",
      "WasteStreams",
      "GetWasteStream",
      "WasteLinesUnified",
      "WasteShipmentCharts",
      "WasteStreamShipmentsByMonth",
    ],
  });

  const getEntityId = useCallback((row: WasteStreamTagsRow) => row.id, []);

  const handleSubmit = useCallback(
    async (updates: EntityUpdate[]) => {
      const { tagsToAdd, tagsToRemove } = wasteStreamTagChanges(selectedRows, updates);
      try {
        if (tagsToAdd.length > 0 || tagsToRemove.length > 0) {
          await updateWasteStreamTags({
            variables: {
              input: { ids: selectedRows.map((row) => row.id), tagsToAdd, tagsToRemove },
            },
          });
        }
        alerts.success(
          `Updated tags on ${selectedRows.length} ${pluralize("waste stream", selectedRows.length)}`
        );
        onSaved?.();
        onClose();
      } catch (err) {
        alerts.error("An error occurred while updating the waste stream tags", err);
      }
    },
    [selectedRows, updateWasteStreamTags, alerts, onSaved, onClose]
  );

  return (
    <ManageTagsFormDialog
      selectedRows={selectedRows}
      getEntityId={getEntityId}
      tenantId={tenantId}
      onClose={onClose}
      onCloseConfirmation={() => setShowConfirmation(false)}
      confirm={() => setShowConfirmation(true)}
      open
      withConfirmation
      showConfirmation={showConfirmation}
      onSubmit={handleSubmit}
      loading={loading}
      entityName="Waste Stream"
      localStorageKey={LOCAL_STORAGE_KEY}
      TagPickerComponent={WasteLineUnifiedTagPicker}
    />
  );
}
